import { Menu, MenuItem } from '@material-ui/core'
import * as React from 'react'
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { CardElement, Elements, IbanElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Meteor } from 'meteor/meteor'

import AccordionSummary from '@material-ui/core/AccordionSummary'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import { makeStyles } from '@material-ui/styles'
import withStyles from '@material-ui/core/styles/withStyles'
import MuiAccordion from '@material-ui/core/Accordion'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { FormattedMessage } from 'react-intl'

import { useGlobalState } from '../store'
import MainLoader from './loadings/MainLoader'
import STRIPE_KEY from '../constants/STRIPE_KEY'

import type { GlobalState } from '../types/globalState'
import { CONTRACT_PAYMENT_TYPE } from '../schema/contracts'

export type PaymentOptionType = {
  id: any,
  name: string,
  type: any,
  logos?: Array<string>,
  detailledView: boolean,
}

export const stripePromise = loadStripe(STRIPE_KEY)

const usePaymentOptionStyles = makeStyles((theme) => ({
  CBDetails: {
    padding: '1vh 1vw',
    backgroundColor: 'white',
  },
  icon: ({ globalTheme }) => ({
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow: 'inset 0 0 0 1px white, inset 0 -1px 0 white',
    backgroundColor: 'white',
    border: `1px solid ${globalTheme.colors.greylight}`,
  }),
  checkedIcon: ({ globalTheme }) => ({
    backgroundColor: globalTheme?.colors.fabPrimary,
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,90%,.1),hsla(0,0%,90%,0))',
    '&:before': {
      display: 'block',
      width: 13,
      height: 13,
      backgroundImage: globalTheme?.colors.fabPrimary,
      content: '""',
    },
  }),
}))

const Accordion = withStyles((theme) => ({
  root: ({ globaltheme }) => ({
    border: `1px solid ${globaltheme.colors.greylight}`,
    borderRadius: '5px',
    backgroundColor: globaltheme.colors?.accordionsBackground,
    boxShadow: 'none',
    marginBottom: '2vh !important',
    [theme.breakpoints.down('xs')]: {
      width: '95% !important',
    },
    '&:before': {
      display: 'none',
      height: '0px !important',
    },
    '&$expanded': {
      margin: 'auto',
    },
  }),
  expanded: {},
}))(MuiAccordion)

export let stripeInstance
export let elementsInstance
export let clientSecretStripe

const StripeInstanceGetter = ({ clientSecret }) => {
  stripeInstance = useStripe()
  elementsInstance = useElements()
  clientSecretStripe = clientSecret

  return null
}

const PaymentOption = ({ option, selectedOption, onClickRadio, onDelete }) => {
  const [theme]: GlobalState = useGlobalState('theme')
  const classes = usePaymentOptionStyles({ globalTheme: theme })
  const [clientSecret, setClientSecret] = useState()
  const [anchorEl, setAnchorEl] = useState(null)

  const isSelected = option?.id === selectedOption?.id

  useEffect(() => {
    if (option?.detailledView && isSelected) {
      if (option.type === CONTRACT_PAYMENT_TYPE.CB && option.detailledView)
        Meteor.promise('preRegisterCard').then(setClientSecret)
      if (option.type === CONTRACT_PAYMENT_TYPE.SEPA && option.detailledView)
        Meteor.promise('preRegisterBankAccount').then(setClientSecret)
    }
  }, [option?.detailledView, isSelected])

  const CBDetails = clientSecret ? (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <div className={classes.CBDetails}>
        {option.type === CONTRACT_PAYMENT_TYPE.CB ? (
          <CardElement
            options={{ hidePostalCode: true, value: { postalCode: Meteor.user().profile.zipCode } }}
          />
        ) : (
          <IbanElement options={{ supportedCountries: ['SEPA'], placeholderCountry: 'FR' }} />
        )}
      </div>

      <StripeInstanceGetter clientSecret={clientSecret} />
    </Elements>
  ) : (
    <MainLoader />
  )

  const renderLabel = (option) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <div>{option?.name}</div>
      <div>
        {(option?.logos || []).map((imgSrc) => (
          <img
            key={imgSrc}
            style={{ width: '20px', height: '18px', marginRight: '.1vw', marginTop: 4 }}
            src={imgSrc}
          />
        ))}
      </div>
    </div>
  )

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget)
  const handleCloseMenu = () => setAnchorEl(null)

  return (
    <Accordion globaltheme={theme} expanded={isSelected}>
      <AccordionSummary style={{ border: 0, borderRadius: '10px' }}>
        <FormControlLabel
          aria-label="Acknowledge"
          onClick={() => onClickRadio && onClickRadio(option)}
          control={
            !!onClickRadio ? (
              <Radio
                color="secondary"
                checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
                icon={<span className={classes.icon} />}
                checked={isSelected}
              />
            ) : (
              <div style={{ marginLeft: 16 }} />
            )
          }
          label={renderLabel(option)}
        />
        {onDelete && (
          <>
            <MoreVertIcon onClick={handleOpenMenu} />
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem
                onClick={() => {
                  onDelete()
                  handleCloseMenu()
                }}
              >
                <FormattedMessage id={'PaymentOption.Delete'} defaultMessage={'Supprimer'} />
              </MenuItem>
            </Menu>
          </>
        )}
      </AccordionSummary>
      {option?.detailledView && (
        <AccordionDetails style={{ backgroundColor: 'transparent', padding: 0, border: 0 }}>
          {CBDetails}
        </AccordionDetails>
      )}
    </Accordion>
  )
}

export default PaymentOption
